import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";
import WhatIfAllocCanvas from "components/WhatIfAllocCanvas";
import CardTools, { ExpandModal } from "components/CardTools";
import { downloadCsv } from "utils/downloadCsv";
import { fmtDec } from "utils/format";

const API   = process.env.REACT_APP_API_URL || "http://localhost:5050/api";
const token = () => sessionStorage.getItem("token");

// ── Constants ─────────────────────────────────────────────────────────────────

const PALETTE = ['#4e8fdd', '#22d47e', '#C9A84C', '#b87bc8', '#e05252'];
const CLASS_KEYS   = ['fi', 'eq', 'alt', 'ma', 'mm', 'ot'];
const CLASS_LABELS = ['Fixed Income', 'Equity', 'Alternatives', 'Multi-Asset', 'Money Market', 'Other'];


function fmtDate(d) {
  if (!d) return '—';
  const parts = d.split('/');
  if (parts.length !== 3) return d;
  const [dd, mm, yy] = parts;
  return `20${yy}-${mm}-${dd}`;
}

function fmtVrisk(v) {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return '$' + (v / 1_000_000).toFixed(1) + 'M';
  return '$' + (v / 1_000).toFixed(1) + 'K';
}

const METRIC_CONFIGS = [
  { key: 'vol',       title: 'Volatility',                suffix: '%'  },
  { key: 'vrisk',     title: 'VaR (95% 1D)',              suffix: '',   formatter: fmtVrisk },
  { key: 'sharpeVol', title: 'Sharpe Ratio (Vol) vs BMK', suffix: ''   },
  { key: 'sharpeVar', title: 'Sharpe Ratio (VaR) vs BMK', suffix: ''   },
];

const ALLOC_CHARTS = [
  { key: 'asset_class', title: 'Asset Allocation — VaR Contribution' },
  { key: 'region',      title: 'Region Allocation — VaR Contribution' },
  { key: 'industry',    title: 'Industry Allocation — VaR Contribution' },
  { key: 'currency',    title: 'Currency Allocation — VaR Contribution' },
];


// ── Metric bar chart ──────────────────────────────────────────────────────────

function MetricChart({ title, data, suffix = "%", limit, formatter }) {
  const chartH = Math.max((data?.length || 0) * 40 + 44, 88);
  const fmtTick    = formatter ? formatter : v => fmtDec(parseFloat(v)) + suffix;
  const fmtTooltip = formatter
    ? v => [formatter(v), '']
    : v => [fmtDec(parseFloat(v)) + suffix, ''];

  return (
    <div className="wif-metric-card">
      <div className="wif-metric-title">{title}</div>
      <div style={{ height: chartH, position: "relative", minWidth: 0 }}>
        <div style={{ position: "absolute", inset: 0 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 56, left: 0, bottom: 0 }}>
            <XAxis
              type="number"
              domain={[0, (dataMax) => Math.max(dataMax, limit || 0) * 1.08]}
              tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)", fontFamily: "'DM Mono',monospace" }}
              tickFormatter={fmtTick}
              axisLine={false} tickLine={false}
            />
            <YAxis
              type="category" dataKey="name" width={90}
              tick={{ fontSize: 10, fill: "rgba(255,255,255,0.6)", fontFamily: "'Outfit',sans-serif" }}
              axisLine={false} tickLine={false}
            />
            <Tooltip
              contentStyle={{
                background: "rgba(8,14,24,0.96)", border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 6, fontSize: 11, fontFamily: "'DM Mono',monospace",
                color: "rgba(255,255,255,0.85)",
              }}
              labelFormatter={() => ''}
              labelStyle={{ display: 'none' }}
              itemStyle={{ color: "rgba(255,255,255,0.85)" }}
              formatter={fmtTooltip}
              separator=""
              cursor={{ fill: "rgba(255,255,255,0.03)" }}
            />
            <Bar dataKey="value" radius={[0, 3, 3, 0]} maxBarSize={18}>
              {(data || []).map((d, i) => (
                <Cell key={i} fill={d.color} fillOpacity={0.8} />
              ))}
            </Bar>
            {limit != null && (
              <ReferenceLine x={limit} stroke="#22d47e" strokeDasharray="5 3" strokeWidth={1.5}
                label={{ value: fmtTick(limit), position: "right", fill: "#22d47e", fontSize: 9, fontFamily: "'DM Mono',monospace" }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}


// ── Concentrations vs Limits canvas ──────────────────────────────────────────
// rows: [{ name, limit, bars: [{ ratio, color }] }]

function ConcCanvas({ rows }) {
  const canvasRef = useRef(null);
  const wrapRef   = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap   = wrapRef.current;
    if (!canvas || !wrap || !rows.length) return;

    const nPorts  = rows[0].bars.length;
    const allRatios = rows.flatMap(r => r.bars.map(b => b.ratio));
    const allLimits = rows.map(r => r.limit);
    const MAX = Math.max(...allRatios, ...allLimits, 0.1) * 1.12;

    const dpr    = window.devicePixelRatio || 1;
    const W      = wrap.getBoundingClientRect().width || 500;
    const barH   = 11, barGap = 3, groupGap = 14;
    const labelW = 90, padTop = 8, axisH = 22, padBot = 4;
    const groupH = nPorts * barH + (nPorts - 1) * barGap;
    const H      = padTop + rows.length * (groupH + groupGap) - groupGap + axisH + padBot;

    canvas.width        = W * dpr;
    canvas.height       = H * dpr;
    canvas.style.width  = W + 'px';
    canvas.style.height = H + 'px';
    canvas.style.display = 'block';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const trackW = W - labelW - 8;
    const tc = a => `rgba(255,255,255,${a})`;
    const gc = () => 'rgba(255,255,255,0.05)';

    rows.forEach((row, di) => {
      const groupY = padTop + di * (groupH + groupGap);

      ctx.font = '500 10px Outfit, sans-serif';
      ctx.fillStyle = tc(0.6);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(row.name, labelW - 6, groupY + groupH / 2);

      row.bars.forEach((bar, pi) => {
        const y    = groupY + pi * (barH + barGap);
        const barW = (bar.ratio / MAX) * trackW;

        ctx.fillStyle = gc();
        ctx.beginPath(); ctx.roundRect(labelW, y, trackW, barH, 2); ctx.fill();

        ctx.fillStyle = bar.color + 'cc';
        ctx.beginPath(); ctx.roundRect(labelW, y, barW, barH, 2); ctx.fill();

        ctx.font = '500 9px DM Mono, monospace';
        ctx.fillStyle = bar.color;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText(fmtDec(bar.ratio) + 'x', labelW + barW + 4, y + barH / 2);
      });

      const lx = labelW + (row.limit / MAX) * trackW;
      ctx.strokeStyle = '#22d47e'; ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath(); ctx.moveTo(lx, groupY - 2); ctx.lineTo(lx, groupY + groupH + 2); ctx.stroke();
      ctx.setLineDash([]);
      ctx.font = '500 8px DM Mono, monospace';
      ctx.fillStyle = '#22d47e'; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
      ctx.fillText(fmtDec(row.limit) + 'x', lx, groupY - 3);
    });

    const axisY = H - axisH + 4;
    ctx.strokeStyle = gc(); ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(labelW, axisY); ctx.lineTo(labelW + trackW, axisY); ctx.stroke();
    ctx.font = '400 9px DM Mono, monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    for (let t = 0; t <= 4; t++) {
      const v  = (t / 4) * MAX;
      const px = labelW + (t / 4) * trackW;
      ctx.strokeStyle = tc(0.2); ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(px, axisY); ctx.lineTo(px, axisY + 3); ctx.stroke();
      ctx.fillStyle = tc(0.3);
      ctx.fillText(fmtDec(v) + 'x', px, axisY + 5);
    }
  }, [rows]);

  useEffect(() => {
    draw();
    const ro = new ResizeObserver(draw);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [draw]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <canvas ref={canvasRef} />
    </div>
  );
}


// ── Clone portfolio modal ─────────────────────────────────────────────────────

function CloneModal({ portfolio, color, onClose }) {
  const navigate = useNavigate();
  const [name, setName]       = useState(`Copy of ${portfolio.name}`);
  const [weights, setWeights] = useState(() => ({ ...portfolio.weights }));
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [error,  setError]  = useState(null);

  const disabled = status === 'loading' || status === 'success';
  const total    = CLASS_KEYS.reduce((s, k) => s + (weights[k] || 0), 0);
  const ok       = Math.abs(total - 100) < 0.01;

  function update(k, raw) {
    const v = Math.max(0, Math.min(100, parseFloat(raw) || 0));
    setWeights(prev => ({ ...prev, [k]: v }));
  }

  async function handleApply() {
    if (!name.trim()) return;
    setStatus('loading');
    setError(null);
    try {
      const res = await fetch(`${API}/portfolios/${portfolio.id}/clone?token=${token()}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name: name.trim(), weights }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || `Server error ${res.status}`);
      setStatus('success');
      setTimeout(() => navigate('/portfolios'), 1500);
    } catch (err) {
      setError(err.message || 'Clone failed');
      setStatus('idle');
    }
  }

  return (
    <div className="wif-modal-overlay active" onClick={e => e.target === e.currentTarget && !disabled && onClose()}>
      <div className="wif-modal">
        <div className="wif-modal-header">
          <div className="wif-modal-title">
            <span className="wif-swatch" style={{ background: color }} />
            Clone Portfolio — {portfolio.name}
          </div>
          <button className="wif-modal-btn wif-btn-cancel" onClick={onClose} disabled={disabled}>✕ close</button>
        </div>

        <div className="wif-modal-body">
          <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: '0.65rem', fontWeight: 500, color: 'var(--dash-text)', marginBottom: '0.25rem' }}>
            Enter a name and adjust the asset class weights for the cloned portfolio.
          </div>

          <div className="wif-modal-row" style={{ marginBottom: '0.75rem' }}>
            <span className="wif-modal-label">New name</span>
            <input
              type="text"
              style={{
                flex: 1, background: 'var(--dash-card2)', border: '1px solid var(--dash-border)',
                borderRadius: 4, color: 'var(--dash-text)', padding: '0.2rem 0.5rem',
                fontFamily: "'Outfit',sans-serif", fontSize: '0.62rem',
              }}
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={disabled}
            />
          </div>

          {CLASS_KEYS.map((k, i) => (
            <div key={k} className="wif-modal-row">
              <span className="wif-modal-label">{CLASS_LABELS[i]}</span>
              <input
                type="range" className="wif-modal-slider" min="0" max="100" step="1"
                value={weights[k] || 0}
                onChange={e => update(k, e.target.value)}
                disabled={disabled}
              />
              <input
                className="wif-modal-input" type="number" min="0" max="100" step="0.1"
                value={weights[k] || 0}
                onChange={e => update(k, e.target.value)}
                disabled={disabled}
              />
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.52rem", color: "var(--dash-muted2)", width: 18, flexShrink: 0 }}>%</span>
            </div>
          ))}

          <div className="wif-modal-total-row">
            <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "0.52rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--dash-muted)" }}>Total</span>
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.75rem", fontWeight: 600, color: ok ? "var(--dash-green)" : "var(--dash-red)" }}>
              {fmtDec(total)}%
            </span>
            <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "0.48rem", color: "var(--dash-muted2)", marginLeft: "auto" }}>
              Weights should sum to 100%
            </span>
          </div>

          {status === 'success' && (
            <div style={{ color: 'var(--dash-green)', fontFamily: "'Outfit',sans-serif", fontSize: '0.62rem', marginTop: '0.6rem', textAlign: 'center' }}>
              Portfolio cloned! Redirecting…
            </div>
          )}
          {error && (
            <div style={{ color: 'var(--dash-red)', fontFamily: "'Outfit',sans-serif", fontSize: '0.58rem', marginTop: '0.5rem' }}>
              {error}
            </div>
          )}
        </div>

        <div className="wif-modal-footer">
          <button className="wif-modal-btn wif-btn-cancel" onClick={onClose} disabled={disabled}>Cancel</button>
          <button
            className="wif-modal-btn wif-btn-apply"
            onClick={handleApply}
            disabled={disabled || !name.trim()}
          >
            {status === 'loading' ? 'Cloning…' : 'Clone'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── PortfolioTab ──────────────────────────────────────────────────────────────

export default function PortfolioTab({
  portfolios, available, allocData,
  portMetrics,
  onAdd, onRemove, theme,
  riskLimits, bmkLimits,
}) {
  console.log("WHATIF_LIMITS", JSON.stringify(riskLimits));
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(-1);
  const [expandOpen, setExpandOpen] = useState(false);

  const handleCsv = () => downloadCsv(
    "portfolio_settings",
    [
      { label: "Portfolio Name", accessor: r => r.name },
      { label: "Size ($mm)",     accessor: r => r.size, fmt: v => v != null ? v.toFixed(2) : "—" },
      { label: "As of",          accessor: r => fmtDate(r.as_of_date) },
      { label: "Risk Horizon",   accessor: r => r.risk_horizon ?? "—" },
      { label: "Risk Measure",   accessor: r => r.risk_measure ?? "—" },
      { label: "Base Currency",  accessor: r => r.base_currency ?? "—" },
      { label: "Benchmark",      accessor: r => r.benchmark ?? "—" },
      { label: "Exp Return",     accessor: r => r.exp_return ?? "—" },
    ],
    portfolios
  );

  useEffect(() => {
    if (!menuOpen) return;
    const h = () => setMenuOpen(false);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [menuOpen]);

  const selectedIds = useMemo(() => new Set(portfolios.map(p => p.id)), [portfolios]);
  const modalPort   = modalIdx >= 0 ? portfolios[modalIdx] : null;

  // Build pre-computed data arrays for metric charts
  const metricsData = useMemo(() => {
    const result = {};
    METRIC_CONFIGS.forEach(({ key }) => {
      result[key] = portfolios
        .filter(p => portMetrics[p.id])
        .map((p, i) => ({
          name:  p.name,
          value: portMetrics[p.id][key],
          color: PALETTE[i % PALETTE.length],
        }))
;
    });
    return result;
  }, [portfolios, portMetrics]);

  // Build rows for ConcCanvas
  const concRows = useMemo(() => {
    const withMetrics = portfolios.filter(p => portMetrics[p.id]?.conc);
    if (!withMetrics.length) return [];
    const template = portMetrics[withMetrics[0].id].conc;
    return template.map((row, i) => ({
      name:  row.name,
      limit: row.limit,
      bars:  withMetrics.map((p, pi) => ({
        ratio: portMetrics[p.id].conc[i].ratio,
        color: PALETTE[pi % PALETTE.length],
      })),
    }));
  }, [portfolios, portMetrics]);

  const hasMetrics = portfolios.some(p => portMetrics[p.id]);

  const portfolioSettingsTable = (
    <table className="wif-table">
      <thead>
        <tr>
          <th style={{ width: '2%' }}></th>
          <th style={{ width: '21%' }}>Portfolio Name</th>
          <th className="right" style={{ width: '8%' }}>Size ($mm)</th>
          <th className="center" style={{ width: '12%' }}>As of</th>
          <th className="center" style={{ width: '10%' }}>Risk Horizon</th>
          <th className="center" style={{ width: '10%' }}>Risk Measure</th>
          <th className="center" style={{ width: '11%' }}>Base Currency</th>
          <th className="center" style={{ width: '10%' }}>Benchmark</th>
          <th className="center" style={{ width: '10%' }}>Exp Return</th>
          <th style={{ width: '6%' }}></th>
        </tr>
      </thead>
      <tbody>
        {portfolios.map((p, i) => {
          const color = PALETTE[i % PALETTE.length];
          return (
            <tr key={p.id} onClick={() => setModalIdx(i)} title="Click to clone portfolio">
              <td><span className="wif-swatch" style={{ background: color }} /></td>
              <td style={{ fontFamily: "'Outfit',sans-serif", fontSize: '0.62rem', fontWeight: 500, color: 'var(--dash-text)' }}>
                {p.name}
              </td>
              <td style={{ textAlign: 'right' }}>{p.size != null ? p.size.toFixed(2) : '—'}</td>
              <td style={{ textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.58rem' }}>{fmtDate(p.as_of_date)}</td>
              <td style={{ textAlign: 'center' }}>{p.risk_horizon ?? '—'}</td>
              <td style={{ textAlign: 'center' }}>{p.risk_measure ?? '—'}</td>
              <td style={{ textAlign: 'center' }}>{p.base_currency ?? '—'}</td>
              <td style={{ textAlign: 'center' }}>{p.benchmark ?? '—'}</td>
              <td style={{ textAlign: 'center' }}>{p.exp_return ?? '—'}</td>
              <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                <button
                  className="wif-remove-btn"
                  style={{ marginRight: 4, padding: '0.1rem 0.4rem', fontSize: '0.52rem', color: 'var(--dash-blue)', opacity: 0.8 }}
                  onClick={e => { e.stopPropagation(); setModalIdx(i); }}
                  title="Clone portfolio"
                >Clone</button>
                {portfolios.length > 1 && (
                  <button className="wif-remove-btn" onClick={() => onRemove(p.id)} title="Remove">×</button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* ── Page header ── */}
      <div className="wif-page-hdr">
        <div>
          <div className="wif-page-title">What-if Analysis</div>
          <div className="wif-page-sub">Compare up to 5 portfolio allocations and their risk/return characteristics</div>
        </div>

        <div style={{ position: 'relative' }}>
          <button
            className={`wif-add-btn${portfolios.length >= 5 ? ' disabled' : ''}`}
            onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Portfolio
            <svg
              width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              style={{ transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
            >
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {menuOpen && (
            <div className="wif-add-menu" onClick={e => e.stopPropagation()}>
              <div className="wif-add-menu-hdr">Available Portfolios</div>
              {available.length === 0 ? (
                <div style={{ padding: '0.5rem 1rem', fontSize: '0.58rem', color: 'var(--dash-muted)', fontFamily: "'Outfit',sans-serif" }}>
                  No portfolios found
                </div>
              ) : available.map(p => {
                const added = selectedIds.has(p.id);
                return (
                  <div
                    key={p.id}
                    className={`wif-add-menu-item${added ? ' added' : ''}`}
                    onClick={() => { if (!added) { onAdd(p); setMenuOpen(false); } }}
                  >
                    <div>
                      <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: '0.62rem', color: 'var(--dash-text)' }}>{p.name}</div>
                      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.5rem', color: 'var(--dash-muted2)' }}>
                        {p.as_of_date} · ${p.size}M
                      </div>
                    </div>
                    {added && <span style={{ fontSize: '0.7rem', color: 'var(--dash-green)', flexShrink: 0 }}>✓</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Portfolio settings table ── */}
      <div className="wif-card trg-tool-host">
        {portfolios.length > 0 && <CardTools onCsv={handleCsv} onExpand={() => setExpandOpen(true)} />}
        <div className="wif-card-header">
          <div className="wif-card-title">Portfolio Settings</div>
          <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: '0.5rem', color: 'var(--dash-muted2)', letterSpacing: '0.06em' }}>
            Click a row or Clone to clone with custom weights · Max 5 portfolios
          </div>
        </div>

        {portfolios.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--dash-muted)', fontFamily: "'Outfit',sans-serif", fontSize: '0.65rem' }}>
            Add a portfolio using the button above to get started
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              {portfolioSettingsTable}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '0.4rem 1rem', borderTop: '1px solid var(--dash-border)' }}>
              {portfolios.map((p, i) => (
                <div key={p.id} className="wif-legend-item">
                  <div style={{ width: 14, height: 6, borderRadius: 2, background: PALETTE[i % PALETTE.length], opacity: 0.8 }} />
                  {p.name}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <ExpandModal open={expandOpen} title="Portfolio Settings" onClose={() => setExpandOpen(false)} onCsv={handleCsv}>
        {portfolioSettingsTable}
      </ExpandModal>

      {/* ── Metrics + charts section ── */}
      {portfolios.length > 0 && hasMetrics && (
        <div className="wif-metrics-section">

          {/* Metric bar charts */}
          <div className="wif-metrics-row">
            {METRIC_CONFIGS.map(({ key, title, suffix, limit, formatter }) => {
              const effLimit =
                key === "vol"       ? riskLimits?.vol_limit_pct :
                key === "vrisk"     ? riskLimits?.var_limit_dollar :
                key === "sharpeVol" ? bmkLimits?.sharpe_vol :
                key === "sharpeVar" ? bmkLimits?.sharpe_var :
                limit;
              return (
                <MetricChart key={key} title={title} data={metricsData[key] || []} suffix={suffix} limit={effLimit != null ? effLimit : undefined} formatter={formatter} />
              );
            })}
          </div>

          {/* Concentrations vs Limits */}
          {concRows.length > 0 && (
            <div className="wif-conc-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div className="wif-conc-title">Concentrations vs Limits</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {portfolios.map((p, i) => (
                    <div key={p.id} className="wif-legend-item">
                      <div style={{ width: 14, height: 6, borderRadius: 2, background: PALETTE[i % PALETTE.length], opacity: 0.8 }} />
                      {p.name}
                    </div>
                  ))}
                </div>
              </div>
              <ConcCanvas rows={concRows} />
            </div>
          )}

          {/* Allocation drilldown charts */}
          {portfolios.some(p => allocData[p.id]) && (
            <div className="wif-alloc-row">
              {ALLOC_CHARTS.map(({ key, title }) => {
                const levelsArray = portfolios.map(p => allocData[p.id]?.[key] ?? null);
                return (
                  <div key={key} className="wif-alloc-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <div className="wif-alloc-title">{title}</div>
                    </div>
                    <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: '0.48rem', color: 'var(--dash-blue)', marginBottom: 4 }}>
                      Click to drill down
                    </div>
                    <WhatIfAllocCanvas levelsArray={levelsArray} palette={PALETTE} theme={theme} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Clone portfolio modal ── */}
      {modalPort && (
        <CloneModal
          portfolio={modalPort}
          color={PALETTE[modalIdx % PALETTE.length]}
          onClose={() => setModalIdx(-1)}
        />
      )}
    </div>
  );
}
